/**
 * The country's policies, where they change a life (net/politics.ts and server/src/country.ts decide them; the game
 * puts the values in force here as the server sends them). Today: income tax on every salary and day's pay.
 *
 * The tax a life pays is counted (`taxOwed`, per country) for the game to hand in to the country's budget on the
 * server; it's taken from the money here and shown in the ledger and the payslip.
 */
import type { LifeState } from './life.ts';
import type { Country } from './travel.ts';

/** what's in force in each country, as the game last heard from the server (none heard: the defaults, nothing taken) */
const IN_FORCE: Partial<Record<Country, Record<string, number>>> = {};
export function setPolicies(country: Country, values: Record<string, number>) { IN_FORCE[country] = { ...values }; }
export const policyIn = (country: Country, id: string, d = 0) => IN_FORCE[country]?.[id] ?? d;
/** income tax there now, as a fraction */
/** a policy that's a share of the usual price, as a multiplier (nothing heard: 1, as ever) */
export const policyShare = (country: Country, id: string) => policyIn(country, id, 100) / 100;
export const incomeTax = (country: Country) => Math.max(0, Math.min(0.3, policyIn(country, 'income_tax') / 100));

type Taxed = LifeState & { taxOwed?: Partial<Record<Country, number>> };
/** the tax waiting to go to each country's budget (the game sends it, then clears what was taken in) */
export const taxOwed = (s: LifeState) => (s as Taxed).taxOwed ?? {};
export function taxHandedIn(s: LifeState, country: Country, amount: number) {
  const t = (s as Taxed).taxOwed ??= {};
  t[country] = Math.max(0, (t[country] ?? 0) - amount);
  if (!t[country]) delete t[country];
}

/**
 * Pay earned at work, in `country`: the whole of it in, then the income tax out (its own line in the ledger).
 * The net, and what was taken.
 */
export function paySalary(s: LifeState, country: Country, amount: number, label: string): { net: number; tax: number } {
  amount = Math.round(amount * policyShare(country, 'public_pay')); // (the national wage top-up, where there is one)
  s.money += amount;
  s.ledger.push({ t: s.minutes, amount, label });
  const tax = Math.round(amount * incomeTax(country));
  if (tax > 0) {
    s.money -= tax;
    s.ledger.push({ t: s.minutes, amount: -tax, label: `Income tax (${Math.round(incomeTax(country) * 100)}%)` });
    const t = (s as Taxed).taxOwed ??= {};
    t[country] = (t[country] ?? 0) + tax;
  }
  return { net: amount - tax, tax };
}
